<div class="dash-shell ks-create">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" [routerLink]="['/entity', entityId, 'dashboard']">Business</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" [routerLink]="['/entity', entityId, 'kickstart']"
        >Kickstart Cash</a
      >
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Apply</span>
    </nav>
  </header>

  <!-- Product hero — frames what Kickstart Cash is and what it takes to apply. -->
  <section class="ks-hero">
    <div class="ks-hero__main">
      <span class="ks-hero__eyebrow">
        <app-icon name="zap" [size]="15" />
        Kickstart Cash
      </span>
      <h1 class="ks-hero__title">Apply for Kickstart Cash</h1>
      <p class="ks-hero__lead">
        A fast working-capital loan from ZAR&nbsp;50,000 to ZAR&nbsp;1,500,000. Tell us how much you
        need, attach a few supporting documents, and submit — we usually come back within a few
        business days.
      </p>
    </div>
    <ul class="ks-hero__facts" aria-label="What you'll need">
      <li class="ks-hero__fact">
        <span class="ks-hero__fact-icon"><app-icon name="wallet" [size]="18" /></span>
        <span class="ks-hero__fact-text">
          <span class="ks-hero__fact-label">Amount</span>
          ZAR 50,000 – ZAR 1,500,000
        </span>
      </li>
      <li class="ks-hero__fact">
        <span class="ks-hero__fact-icon"><app-icon name="document" [size]="18" /></span>
        <span class="ks-hero__fact-text">
          <span class="ks-hero__fact-label">Documents</span>
          Bank statements &amp; financials
        </span>
      </li>
      <li class="ks-hero__fact">
        <span class="ks-hero__fact-icon"><app-icon name="clock" [size]="18" /></span>
        <span class="ks-hero__fact-text">
          <span class="ks-hero__fact-label">Decision</span>
          A few business days
        </span>
      </li>
    </ul>
  </section>

  <!-- Stepper — completed steps are tappable to go back. -->
  <ol class="ks-stepper" aria-label="Application steps">
    @for (s of steps(); track s.id; let i = $index) {
      <li
        class="ks-stepper__item"
        [class.ks-stepper__item--done]="stepState(s.id) === 'done'"
        [class.ks-stepper__item--active]="stepState(s.id) === 'active'"
        [attr.aria-current]="stepState(s.id) === 'active' ? 'step' : null"
      >
        <button
          type="button"
          class="ks-stepper__btn"
          [disabled]="stepState(s.id) !== 'done'"
          (click)="goToStep(s.id)"
        >
          <span class="ks-stepper__marker">
            @if (stepState(s.id) === 'done') {
              <app-icon name="check" [size]="14" />
            } @else {
              {{ i + 1 }}
            }
          </span>
          <span class="ks-stepper__label">{{ s.label }}</span>
        </button>
      </li>
    }
  </ol>

  @if (serverError(); as error) {
    <div class="ks-banner ks-banner--error" role="alert">
      <app-icon name="x-circle" [size]="18" />
      <span>{{ error }}</span>
    </div>
  }

  @if (consent() === undefined) {
    <div class="ks-loading" aria-live="polite">
      <mat-spinner diameter="28" />
    </div>
  }

  <!--
    STEP 0 — the business's AI-assessment consent, only where it is not on file
    or was withdrawn (DOC-72 UI-DA-72-01, FC-DET-051). Its own question and its
    own act: "Yes" POSTs the consent endpoint; Continue and Submit record nothing.
    The words are the build's current ones (ai-consent-checkbox.html), a
    placeholder until Legal's (DOC-72 §2).
  -->
  @if (consent() !== undefined && step() === 'consent') {
    <mat-card appearance="outlined" class="ks-card">
      <mat-card-content>
        <div class="ks-card__head">
          <h2 class="ks-card__title">Consent to AI-assisted assessment</h2>
          @if (consent()?.ai_consent?.state === 'WITHDRAWN') {
            <p class="ks-card__hint">
              The business's consent was withdrawn on
              {{ consent()?.ai_consent?.revoked_at | date: 'd MMM y' }}, so the question is put
              again.
            </p>
          }
        </div>
        <p class="ks-consent-text">
          CapX uses AI providers, which may be outside South Africa and which CapX will ensure comply with POPIA, to check the documents submitted for a business and produce an advisory assessment for the CapX team. A person always makes the final decision. One consent, given for the business, covers every funding application made for it until the consent is withdrawn.
        </p>
        <mat-radio-group
          class="ks-choice"
          aria-label="Does the business consent to AI-assisted assessment?"
          [value]="consentAnswer()"
          (change)="consentAnswer.set($event.value)"
          data-testid="consent-question"
        >
          <mat-radio-button value="YES">Yes, the business consents</mat-radio-button>
          <mat-radio-button value="NO">No, not now</mat-radio-button>
        </mat-radio-group>
        @if (consentAnswer() === 'NO') {
          <p class="ks-card__hint">
            You can still fill in the details. Statements can be uploaded once the business's
            consent is given.
          </p>
        }
        <div class="ks-actions">
          <a mat-stroked-button [routerLink]="['/entity', entityId, 'dashboard']">Cancel</a>
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="continueFromConsent()"
            [disabled]="consentAnswer() === null || isGivingConsent()"
            data-testid="consent-continue"
          >
            @if (isGivingConsent()) {
              <mat-spinner diameter="20" />
            } @else {
              <span>Continue</span>
              <app-icon name="arrow-right" [size]="18" />
            }
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  }

  <!-- STEP 1 — Details -->
  @if (consent() !== undefined && step() === 'details') {
    <mat-card appearance="outlined" class="ks-card">
      <mat-card-content>
        <div class="ks-card__head">
          <h2 class="ks-card__title">How much do you need?</h2>
          <p class="ks-card__hint">
            Pick a suggested amount or enter your own. You can request between ZAR 50,000 and ZAR
            1,500,000.
          </p>
        </div>

        <form [formGroup]="form" (ngSubmit)="createDraft()" class="ks-form">
          <div class="ks-presets" role="group" aria-label="Suggested amounts">
            @for (preset of AMOUNT_PRESETS; track preset) {
              <button
                type="button"
                class="ks-preset"
                [class.ks-preset--selected]="form.controls.amount.value === preset"
                (click)="pickAmount(preset)"
              >
                {{ preset | zar }}
              </button>
            }
          </div>

          <mat-form-field appearance="outline" class="ks-form__field">
            <mat-label>Amount requested</mat-label>
            <span matTextPrefix class="ks-form__prefix">ZAR&nbsp;</span>
            <input
              matInput
              type="number"
              formControlName="amount"
              [min]="KS_MIN"
              [max]="KS_MAX"
              data-testid="amount-input"
            />
            <mat-hint>Between ZAR 50,000 and ZAR 1,500,000</mat-hint>
            @if (form.controls.amount.touched && form.controls.amount.hasError('required')) {
              <mat-error>Enter the amount you need.</mat-error>
            }
            @if (form.controls.amount.touched && form.controls.amount.hasError('min')) {
              <mat-error>Minimum amount is ZAR 50,000.</mat-error>
            }
            @if (form.controls.amount.touched && form.controls.amount.hasError('max')) {
              <mat-error>Maximum amount is ZAR 1,500,000.</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="ks-form__field">
            <mat-label>What's it for? (optional)</mat-label>
            <textarea
              matInput
              formControlName="purpose"
              rows="3"
              placeholder="e.g. Bridge a large purchase order, restock inventory, cover payroll"
            ></textarea>
            <mat-hint>A short note helps us assess your application faster.</mat-hint>
          </mat-form-field>

          <!-- DOC-72 UI-DA-72-02: sets the months asked for; checks nothing. -->
          <fieldset class="ks-question">
            <legend class="ks-question__label">
              Does the business have financial statements to upload (annual or management accounts)?
            </legend>
            <mat-radio-group
              class="ks-choice"
              formControlName="hasFinancials"
              data-testid="financials-question"
            >
              <mat-radio-button [value]="true">Yes</mat-radio-button>
              <mat-radio-button [value]="false">No</mat-radio-button>
            </mat-radio-group>
            @if (months() === 6) {
              <p class="ks-question__note" data-testid="route-note">
                You'll upload them with at least the last 6 months of bank statements.
              </p>
            } @else if (months() === 12) {
              <p class="ks-question__note" data-testid="route-note">
                You can still apply on bank statements alone. You'll need at least the last 12
                months of bank statements, and the business needs to have been trading for at least
                12 months. This is set when you submit and can't be changed later without a new
                application.
              </p>
            }
            @if (form.controls.hasFinancials.touched && form.controls.hasFinancials.invalid) {
              <p class="ks-question__error" role="alert">Choose an answer to continue.</p>
            }
          </fieldset>

          <div class="ks-actions">
            @if (consentAsked()) {
              <button mat-stroked-button type="button" (click)="back()">
                <app-icon name="arrow-left" [size]="18" />
                <span>Back</span>
              </button>
            } @else {
              <a mat-stroked-button [routerLink]="['/entity', entityId, 'dashboard']">Cancel</a>
            }
            <button
              mat-flat-button
              color="primary"
              type="submit"
              [disabled]="isSaving()"
              data-testid="continue-btn"
            >
              @if (isSaving()) {
                <mat-spinner diameter="20" />
              } @else {
                <span>Continue</span>
                <app-icon name="arrow-right" [size]="18" />
              }
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>
  }

  <!-- STEP 2 — Documents (DOC-72 UI-DA-72-03, 04, 08) -->
  @if (step() === 'documents') {
    <mat-card appearance="outlined" class="ks-card">
      <mat-card-content>
        <div class="ks-card__head">
          <h2 class="ks-card__title">Supporting documents</h2>
          <p class="ks-card__hint" data-testid="statements-ask">
            Upload statements for every business bank account the business uses — the last
            {{ months() ?? 6 }} months for each. We check each file as it uploads and give you a
            quick signal on whether it looks right.
          </p>
        </div>

        @if (consentOnFile()) {
          <p class="ks-consent-line" data-testid="consent-on-file">
            <app-icon name="shield" [size]="16" />
            <span>
              The business's consent to AI assessment is on file — given
              {{ consent()?.ai_consent?.consented_at | date: 'd MMM y' }}{{ givenBy() }}.
            </span>
          </p>
        } @else {
          <div class="ks-banner ks-banner--warning" role="status" data-testid="uploads-blocked">
            <app-icon name="alert-triangle" [size]="18" />
            <span>
              Statements can be uploaded once the business's consent to AI assessment is given.
              <button mat-button type="button" (click)="step.set('consent')">
                Answer the consent question
              </button>
            </span>
          </div>
        }

        <div class="ks-drops">
          <app-doc-upload-panel
            [docTypes]="docTypes()"
            [shortId]="draft()?.short_id ?? null"
            [initialDocs]="initialDocs()"
            [disabled]="uploadsBlocked()"
            [uploadFn]="uploadDoc"
            [deleteFn]="deleteDoc"
            [fetchDocsFn]="fetchDocs"
            (inFlightChange)="anyInFlight.set($event)"
            (attachedDocsChange)="attachedDocs.set($event)"
            (failedUploadsChange)="failedUploads.set($event)"
          />
        </div>

        <!-- The every-account confirmation: required; no honesty or disclosure words (FC-DET-047). -->
        <fieldset class="ks-question" data-testid="accounts-question">
          <legend class="ks-question__label">
            Have you uploaded statements for every business bank account the business uses?
          </legend>
          <mat-radio-group
            class="ks-choice"
            [value]="accountsAnswer()"
            (change)="setAccountsAnswer($event.value)"
          >
            <mat-radio-button value="ALL_UPLOADED"
              >Yes — every account is uploaded</mat-radio-button
            >
            <mat-radio-button value="NOT_ALL">No — some are not uploaded</mat-radio-button>
          </mat-radio-group>

          @if (accountsAnswer() === 'NOT_ALL') {
            <p class="ks-question__note">For each account not uploaded:</p>
            @for (row of accountRows(); track $index; let i = $index) {
              <div class="ks-account-row" data-testid="account-row">
                <mat-form-field appearance="outline" class="ks-account-row__bank">
                  <mat-label>Which bank</mat-label>
                  <input
                    matInput
                    [value]="row.bank_name"
                    (input)="updateAccountRow(i, 'bank_name', $any($event.target).value)"
                  />
                </mat-form-field>
                <mat-form-field appearance="outline" class="ks-account-row__digits">
                  <mat-label>Last four (if known)</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    maxlength="4"
                    [value]="row.last_four"
                    (input)="updateAccountRow(i, 'last_four', $any($event.target).value)"
                  />
                </mat-form-field>
                <mat-form-field appearance="outline" class="ks-account-row__reason">
                  <mat-label>Why it is not uploaded</mat-label>
                  <input
                    matInput
                    [value]="row.reason"
                    (input)="updateAccountRow(i, 'reason', $any($event.target).value)"
                  />
                </mat-form-field>
                @if (accountRows().length > 1) {
                  <button
                    mat-icon-button
                    type="button"
                    aria-label="Remove this account"
                    (click)="removeAccountRow(i)"
                  >
                    <app-icon name="x" [size]="18" />
                  </button>
                }
              </div>
            }
            @if (accountRows().length === 0 || !confirmationValid()) {
              <p class="ks-question__note">
                Add each account with its bank and why it is not uploaded; the last four digits must
                be four numbers, or left blank.
              </p>
            }
            <button mat-stroked-button type="button" (click)="addAccountRow()">
              <app-icon name="plus" [size]="16" />
              <span>Add another account</span>
            </button>
          }
        </fieldset>

        <!-- Tell us about your business — optional, never blocks (DOC-72 UI-DA-72-08). -->
        @if (draft(); as d) {
          <app-ks-context-block
            class="ks-context"
            [shortId]="d.short_id"
            [initialEntries]="contextEntries()"
            (entriesChange)="contextEntries.set($event)"
            (busyChange)="contextBusy.set($event)"
          />
        }

        <div class="ks-actions">
          <button mat-stroked-button type="button" (click)="back()">
            <app-icon name="arrow-left" [size]="18" />
            <span>Back</span>
          </button>
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="toReview()"
            [disabled]="anyInFlight() || contextBusy()"
            data-testid="to-review-btn"
          >
            @if (anyInFlight()) {
              <mat-spinner diameter="20" />
              <span>Checking documents…</span>
            } @else {
              <span>Review &amp; submit</span>
              <app-icon name="arrow-right" [size]="18" />
            }
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  }

  <!-- STEP 3 — Review -->
  @if (step() === 'review') {
    <mat-card appearance="outlined" class="ks-card">
      <mat-card-content>
        <div class="ks-card__head">
          <h2 class="ks-card__title">Review your application</h2>
          <p class="ks-card__hint">
            Check the details below. Once you submit, our team will assess your application.
          </p>
        </div>

        <dl class="ks-review">
          <div class="ks-review__row ks-review__row--amount">
            <dt>Amount requested</dt>
            <dd class="ks-review__amount">{{ form.controls.amount.value | zar }}</dd>
          </div>
          @if (form.controls.purpose.value?.trim()) {
            <div class="ks-review__row">
              <dt>Purpose</dt>
              <dd>{{ form.controls.purpose.value }}</dd>
            </div>
          }
          <div class="ks-review__row">
            <dt>Route</dt>
            <dd data-testid="review-route">{{ routeLabel() }}</dd>
          </div>
          <div class="ks-review__row">
            <dt>Documents</dt>
            <dd>
              @if (attachedDocs().length > 0) {
                <ul class="ks-review__docs">
                  @for (d of attachedDocs(); track d.short_id) {
                    <li>
                      <app-icon name="file-text" [size]="15" />
                      <span>{{ d.file_name }}</span>
                    </li>
                  }
                </ul>
              } @else {
                <span class="ks-review__muted">None attached.</span>
              }
            </dd>
          </div>
          <div class="ks-review__row">
            <dt>Bank accounts</dt>
            <dd data-testid="review-accounts">
              @switch (accountsAnswer()) {
                @case ('ALL_UPLOADED') {
                  Yes — every account is uploaded
                }
                @case ('NOT_ALL') {
                  No — some are not uploaded
                  <ul class="ks-review__docs">
                    @for (r of accountRows(); track $index) {
                      <li>
                        {{ r.bank_name }}{{ r.last_four ? ' ···' + r.last_four : '' }} —
                        {{ r.reason }}
                      </li>
                    }
                  </ul>
                }
                @default {
                  <span class="ks-review__muted">Not answered</span>
                }
              }
            </dd>
          </div>
          <div class="ks-review__row">
            <dt>AI-assessment consent</dt>
            <dd>
              @if (consentOnFile()) {
                On file — given {{ consent()?.ai_consent?.consented_at | date: 'd MMM y' }}
                @if (consent()?.given_by_name) {
                  by {{ consent()?.given_by_name }}
                }
              } @else {
                <span class="ks-review__muted">Not given</span>
              }
            </dd>
          </div>
          <div class="ks-review__row">
            <dt>About the business</dt>
            <dd data-testid="review-context">
              @if (contextEntries().length > 0) {
                <ul class="ks-review__docs">
                  @for (e of contextEntries(); track e.short_id) {
                    <li>{{ e.text || e.file_name }}</li>
                  }
                </ul>
              } @else {
                <span class="ks-review__muted">— Nothing added.</span>
                <button mat-button type="button" (click)="addANote()">Add a note</button>
              }
            </dd>
          </div>
        </dl>

        @if (missing().length > 0) {
          <ul class="ks-missing" role="status" data-testid="review-missing">
            @for (line of missing(); track line) {
              <li>{{ line }}</li>
            }
          </ul>
        }

        @if (failedUploads().length > 0) {
          <div class="ks-banner ks-banner--warning" role="alert" data-testid="failed-uploads">
            <app-icon name="alert-triangle" [size]="18" />
            <span>
              @if (failedUploads().length === 1) {
                This file did not upload and will not be sent:
              } @else {
                These {{ failedUploads().length }} files did not upload and will not be sent:
              }
              <strong>
                @for (name of failedUploads(); track $index; let last = $last) {
                  {{ name }}{{ last ? '' : ', ' }}
                }</strong
              >. Go back to Documents to try again.
            </span>
          </div>
        }

        <!-- DOC-72 UI-DA-72-06: confirms correctness only; records no consent. -->
        <p class="ks-consent">
          <app-icon name="shield" [size]="16" />
          <span>
            By submitting, you confirm that the information and documents you've given are correct
            as far as you know.
          </span>
        </p>

        <div class="ks-actions">
          <button mat-stroked-button type="button" (click)="back()">
            <app-icon name="arrow-left" [size]="18" />
            <span>Back</span>
          </button>
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="submit()"
            [disabled]="isSubmitting() || missing().length > 0"
            data-testid="submit-btn"
          >
            @if (isSubmitting()) {
              <mat-spinner diameter="20" />
            } @else {
              <app-icon name="check" [size]="18" />
              <span>Submit application</span>
            }
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  }
</div>
